import { Navigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import { isAdmin, isCurrentUser } from '../../helpers/utils'
import { ReactNode, useEffect, useState } from 'react'
import { CommunityDTO } from '../../models/community'
import CustomTabs, { CustomTab } from '../../containers/Tabs'
import CommunitiesService from '../../services/CommunitiesService'
import CommunityCard from './CommunityCard'
import CommunityForm from './CommunityForm'
import CustomButton from '../../components/Button'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CustomModal from '../../containers/Modal'
import NoResults from '../../containers/NoResults'
import CustomPaper from '../../containers/Paper'
import { CircularProgress } from '@mui/material'

const CommunitiesPage = () => {
    const [selectedTab, setSelectedTab] = useState<string>('Mis comunidades')
    const [myCommunities, setMyCommunities] = useState<CommunityDTO[]>()
    const [myPendingRequests, setMyPendingRequests] = useState<CommunityDTO[]>()
    const [publicCommunities, setPublicCommunities] = useState<CommunityDTO[]>()
    const [privateCommunities, setPrivateCommunities] = useState<CommunityDTO[]>()
    const [isCommunityFormOpen, setIsCommunityFormOpen] = useState<boolean>(false)
    const [mutate, setMutate] = useState<boolean>(false)

    useEffect(() => {
        const communitiesService = new CommunitiesService()
        communitiesService.getMyCommunities().then((response) => {
            if (response.status !== 200) return
            setMyCommunities(response.data ?? [])
        })
        communitiesService.getMyPendingRequests().then((response) => {
            if (response.status !== 200) return
            setMyPendingRequests(response.data ?? [])
        })
        communitiesService.getPublicCommunities().then((response) => {
            if (response.status !== 200) return
            setPublicCommunities(response.data ?? [])
        })
        communitiesService.getPrivateCommunities().then((response) => {
            if (response.status !== 200) return
            setPrivateCommunities(response.data ?? [])
        })
    }, [mutate])

    const communitiesList = (communities: CommunityDTO[], emptyMessage: ReactNode) => {
        const userIsMember = (community: CommunityDTO) =>
            community.members.some((user) => isCurrentUser(user))

        return (
            <div className="flex flex-col w-full gap-4 mt-2 md:mt-4">
                <CustomButton
                    startIcon={<AddCircleOutlineIcon />}
                    className="w-fit"
                    secondary
                    onClick={() => setIsCommunityFormOpen(true)}
                >
                    Crear comunidad
                </CustomButton>
                {communities.length > 0 ? (
                    <div className="flex flex-col gap-4">
                        {communities.map((x) => (
                            <div key={x.id}>
                                <CommunityCard
                                    community={x}
                                    userIsMember={userIsMember(x)}
                                    mutate={mutate}
                                    setMutate={setMutate}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    emptyMessage
                )}
            </div>
        )
    }

    const tabs: CustomTab[] = [
        {
            value: 'Mis comunidades',
            label: (
                <div className="flex gap-1 items-center text-[10px] md:text-base">
                    Mis comunidades
                </div>
            ),
            content: myCommunities ? (
                communitiesList(
                    myCommunities,
                    <CustomPaper>
                        <NoResults
                            title="No pertenecés a comunidades aún"
                            subtitle="Podés crear una nueva, unirte a una comunidad pública o solicitar ingreso a una comunidad privada"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
        },
        {
            value: 'Comunidades públicas',
            label: (
                <div className="flex gap-1 items-center text-[10px] md:text-base">
                    Comunidades públicas
                </div>
            ),
            content: publicCommunities ? (
                communitiesList(
                    publicCommunities,
                    <CustomPaper>
                        <NoResults
                            title="No se encontraron comunidades públicas"
                            subtitle="¡Sé el primero en crear una!"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
        },
        {
            value: 'Comunidades privadas',
            label: (
                <div className="flex gap-1 items-center text-[10px] md:text-base">
                    Comunidades privadas
                </div>
            ),
            content: privateCommunities ? (
                communitiesList(
                    privateCommunities,
                    <CustomPaper>
                        <NoResults
                            title="No se encontraron comunidades privadas"
                            subtitle="¡Sé el primero en crear una!"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
        },
        {
            value: 'Solicitudes pendientes',
            label: (
                <div className="flex gap-1 items-center text-[10px] md:text-base">
                    Solicitudes pendientes
                </div>
            ),
            content: myPendingRequests ? (
                communitiesList(
                    myPendingRequests,
                    <CustomPaper>
                        <NoResults
                            title="No tenés solicitudes de ingreso pendientes"
                            subtitle="Si querés ingresar a una comunidad deberás solicitar ingreso primero"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
        },
    ]

    return (
        <>
            {isAdmin() ? (
                <Navigate to={AppRoutes.news} />
            ) : (
                <>
                    <CustomTabs
                        tabs={tabs}
                        value={selectedTab}
                        setValue={setSelectedTab}
                        gap={4}
                        type="buttons"
                    />
                    <CustomModal
                        isOpen={isCommunityFormOpen}
                        handleClose={() => setIsCommunityFormOpen(false)}
                    >
                        <CommunityForm
                            handleClose={() => setIsCommunityFormOpen(false)}
                            mutate={mutate}
                            setMutate={setMutate}
                        />
                    </CustomModal>
                </>
            )}
        </>
    )
}

export default CommunitiesPage
